import React from "react";
import PropTypes from "prop-types";
import Modal from "@mui/material/Modal";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import "../organisation/organization.css";
import {
  fontFamily,
  reviewScreenButtonWithColor,
  reviewScreenButtonWithOutColor,
} from "../Constants/constants";
import { InputBase } from "@mui/material";

export default function OrganisationModel({ show, handleClose, onSave, editingData }) {
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [nameError, setNameError] = React.useState("");
  const [descriptionError, setDescriptionError] = React.useState("");

  // New organization settings fields
  const [sendToAdminAPI, setSendToAdminAPI] = React.useState(false);
  const [complyAdminUrl, setComplyAdminUrl] = React.useState("");
  const [complyAdminKey, setComplyAdminKey] = React.useState("");
  const [complyAdminUrlError, setComplyAdminUrlError] = React.useState("");
  const [complyAdminKeyError, setComplyAdminKeyError] = React.useState("");
  const [autoSuspendUserAfterDays, setAutoSuspendUserAfterDays] = React.useState(10);

  React.useEffect(() => {
    if (editingData) {
      console.log("editingData in model", editingData);
      setDescription(editingData.description);
      setName(editingData.name);
      // Convert string "true"/"false" to boolean
      setSendToAdminAPI(editingData.sendToAdminURL === "true" ? true : false);
      setComplyAdminUrl(editingData.complyAdminUrl || "");
      setComplyAdminKey(editingData.complyAdminKey || "");
      setAutoSuspendUserAfterDays(editingData.autoSuspendUserAfterDays || 10);
    } else {
      setDescription("");
      setName("");
      setSendToAdminAPI(false);
      setComplyAdminUrl("");
      setComplyAdminKey("");
      setAutoSuspendUserAfterDays(10);
    }
    // Clear errors when modal opens
    setNameError("");
    setDescriptionError("");
    setComplyAdminUrlError("");
    setComplyAdminKeyError("");
  }, [editingData, show]);

  const validateForm = () => {
    let isValid = true;

    // Validate name
    if (!name || name.trim() === "") {
      setNameError("Name is required");
      isValid = false;
    } else if (name.trim().length < 2) {
      setNameError("Name must be at least 2 characters long");
      isValid = false;
    } else {
      setNameError("");
    }

    // Validate description
    if (!description || description.trim() === "") {
      setDescriptionError("Description is required");
      isValid = false;
    } else if (description.trim().length < 5) {
      setDescriptionError("Description must be at least 5 characters long");
      isValid = false;
    } else {
      setDescriptionError("");
    }

    // Validate Comply Admin fields if Send To Admin API is enabled
    if (sendToAdminAPI) {
      if (!complyAdminUrl || complyAdminUrl.trim() === "") {
        setComplyAdminUrlError("Comply Admin URL is required when Send To Admin API is enabled");
        isValid = false;
      } else {
        setComplyAdminUrlError("");
      }

      // Comply Admin Key is required only when creating (not editing)
      if (!editingData) {
        if (!complyAdminKey || complyAdminKey.trim() === "") {
          setComplyAdminKeyError("Comply Admin Key is required when Send To Admin API is enabled");
          isValid = false;
        } else {
          setComplyAdminKeyError("");
        }
      } else {
        // During edit, key is optional (hidden on backend)
        setComplyAdminKeyError("");
      }
    } else {
      setComplyAdminUrlError("");
      setComplyAdminKeyError("");
    }

    return isValid;
  };

  const handleSaveClick = () => {
    if (validateForm()) {
      // Get user info from localStorage
      const userData = JSON.parse(localStorage.getItem("token")) || {};
      const userEmail = userData.email || "";

      const newRow = {
        name: name.trim(),
        description: description.trim(),
        createdBy: userEmail,
        complyAdminUrl: sendToAdminAPI ? complyAdminUrl.trim() : "",
        pythonAPI: "", // Keep empty as requested
        sendToAdminAPI: sendToAdminAPI ? "true" : "false",
        // For edit: only include key if user entered a new value
        // For create: include key if sendToAdminAPI is enabled
        complyAdminKey: sendToAdminAPI ? complyAdminKey.trim() : "",
        autoSuspendUserAfterDays: parseInt(autoSuspendUserAfterDays) || 90,
      };
      onSave(newRow);
    }
  };

  const handleNameChange = (e) => {
    setName(e.target.value);
    if (nameError) {
      setNameError("");
    }
  };

  const handleDescriptionChange = (e) => {
    setDescription(e.target.value);
    if (descriptionError) {
      setDescriptionError("");
    }
  };

  const borderStyle = {
    width: "250px",
    marginLeft: "20px",
    backgroundColor: "white",
    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
      borderColor: "#2aa7e0",
    },
    "&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
      borderColor: "#2aa7e0",
    },
    "&.Mui-focused .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
      borderColor: "#2aa7e0",
    },
  };

  const errorBorderStyle = {
    width: "250px",
    marginLeft: "20px",
    backgroundColor: "white",
    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
      borderColor: "#d32f2f",
    },
    "&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
      borderColor: "#d32f2f",
    },
    "&.Mui-focused .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
      borderColor: "#d32f2f",
    },
  };

  return (
    <Modal
      open={show}
      onClose={handleClose}
      aria-labelledby="add-administrator-modal-title"
      aria-describedby="add-administrator-modal-description"
    >
      <div
        style={{
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          position: "absolute",
          outline: "none",
          width: "450px", // Width changed to 500px
          padding: "20px",
          backgroundColor: "white",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
        }}
      >
        <h6 style={{ color: "#2aa7e0", fontFamily: fontFamily }}>
          {editingData ? "Edit Organisation" : "Add New Organisation"}
        </h6>
        <div
          style={{
            height: "1px",
            width: "410px",
            backgroundColor: "#2aa7e0",
            marginBottom: "20px",
            marginRight: "10px",
          }}
        ></div>

        <div>
          {/* Adjusting height of text boxes */}
          <div style={{ marginBottom: "20px", display: "flex", flexDirection: "row" }}>
            <span
              style={{
                color: "black",
                display: "inline-block",
                width: "105px",
                fontFamily: fontFamily,
              }}
            >
              Name <span style={{ color: "red" }}>*</span>
            </span>
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <InputBase
                value={name}
                sx={nameError ? errorBorderStyle : borderStyle}
                onChange={handleNameChange}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                }}
                className="text-input"
                disabled={false}
              />
              {nameError && (
                <span
                  style={{
                    color: "#d32f2f",
                    fontSize: "12px",
                    marginTop: "4px",
                    marginLeft: "20px",
                  }}
                >
                  {nameError}
                </span>
              )}
            </div>
          </div>
          <div style={{ marginBottom: "20px", display: "flex", flexDirection: "row" }}>
            <span
              style={{
                color: "black",
                display: "inline-block",
                width: "105px",
                fontFamily: fontFamily,
              }}
            >
              Description <span style={{ color: "red" }}>*</span>
            </span>
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <InputBase
                value={description}
                sx={descriptionError ? errorBorderStyle : borderStyle}
                onChange={handleDescriptionChange}
                InputProps={{
                  disableUnderline: true,
                }}
                variant="standard"
                className="text-input"
                disabled={false}
              />
              {descriptionError && (
                <span
                  style={{
                    color: "#d32f2f",
                    fontSize: "12px",
                    marginTop: "4px",
                    marginLeft: "20px",
                  }}
                >
                  {descriptionError}
                </span>
              )}
            </div>
          </div>

          <div
            style={{
              marginBottom: "20px",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <span
              style={{
                color: "black",
                display: "inline-block",
                width: "105px",
                fontFamily: fontFamily,
              }}
            >
              Send To Admin API
            </span>
            <Checkbox
              checked={sendToAdminAPI}
              onChange={(e) => {
                setSendToAdminAPI(e.target.checked);
                // Clear values when unchecked
                if (!e.target.checked) {
                  setComplyAdminUrl("");
                  setComplyAdminKey("");
                  setComplyAdminUrlError("");
                  setComplyAdminKeyError("");
                }
              }}
              style={{ color: "#2aa7e0", padding: "4px" }}
            />
          </div>

          {sendToAdminAPI && (
            <>
              <div style={{ marginBottom: "20px", display: "flex", flexDirection: "row" }}>
                <span
                  style={{
                    color: "black",
                    display: "inline-block",
                    width: "105px",
                    fontFamily: fontFamily,
                  }}
                >
                  Comply Admin URL <span style={{ color: "red" }}>*</span>
                </span>
                <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                  <InputBase
                    value={complyAdminUrl}
                    sx={complyAdminUrlError ? errorBorderStyle : borderStyle}
                    onChange={(e) => {
                      setComplyAdminUrl(e.target.value);
                      if (complyAdminUrlError) {
                        setComplyAdminUrlError("");
                      }
                    }}
                    InputProps={{
                      disableUnderline: true,
                    }}
                    variant="standard"
                    className="text-input"
                    disabled={false}
                    placeholder="https://admin.example.com"
                  />
                  {complyAdminUrlError && (
                    <span
                      style={{
                        color: "#d32f2f",
                        fontSize: "12px",
                        marginTop: "4px",
                        marginLeft: "20px",
                      }}
                    >
                      {complyAdminUrlError}
                    </span>
                  )}
                </div>
              </div>

              {/* Only show Comply Admin Key field when creating (not editing) */}
              {!editingData && (
                <div style={{ marginBottom: "20px", display: "flex", flexDirection: "row" }}>
                  <span
                    style={{
                      color: "black",
                      display: "inline-block",
                      width: "105px",
                      fontFamily: fontFamily,
                    }}
                  >
                    Comply Admin Key <span style={{ color: "red" }}>*</span>
                  </span>
                  <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                    <InputBase
                      value={complyAdminKey}
                      sx={complyAdminKeyError ? errorBorderStyle : borderStyle}
                      onChange={(e) => {
                        setComplyAdminKey(e.target.value);
                        if (complyAdminKeyError) {
                          setComplyAdminKeyError("");
                        }
                      }}
                      InputProps={{
                        disableUnderline: true,
                      }}
                      variant="standard"
                      className="text-input"
                      disabled={false}
                      placeholder="Enter API key"
                    />
                    {complyAdminKeyError && (
                      <span
                        style={{
                          color: "#d32f2f",
                          fontSize: "12px",
                          marginTop: "4px",
                          marginLeft: "20px",
                        }}
                      >
                        {complyAdminKeyError}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
          <div style={{ marginBottom: "20px", display: "flex", flexDirection: "row" }}>
            <span
              style={{
                color: "black",
                display: "inline-block",
                width: "105px",
                fontFamily: fontFamily,
              }}
            >
              Auto Suspend User After Days
            </span>
            <InputBase
              value={autoSuspendUserAfterDays}
              sx={borderStyle}
              onChange={(e) => setAutoSuspendUserAfterDays(e.target.value)}
              InputProps={{
                disableUnderline: true,
              }}
              variant="standard"
              className="text-input"
              disabled={false}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            {/* Adjusting button styles as per requirements */}
            <Button
              variant="contained"
              style={reviewScreenButtonWithOutColor(100)}
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              style={reviewScreenButtonWithColor(80)}
              onClick={handleSaveClick}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

OrganisationModel.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  editingData: PropTypes.func.isRequired,
};
